import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { extractCategoryFilters } from '@app/src/utils/filterUtils'
import React from 'react'
import { useIntl } from 'react-intl'
import FilterDisplayChip from '../components/FilterDisplayChip'
import StyledChip from '../components/StyledChip'
import {
  COUNTRY_INDEX_PROP,
  COUNTRY_RISK_PROP,
  INDUSTRY_INDEX_PROP,
  INDUSTRY_RISK_PROP,
} from './getAssessmentFilterLabel'

interface RiskStatusOption {
  label: string
  value: string | number
}

export const useGetAutomatedAssessmentChips = (
  categoryFilters: FilterGroup[] | undefined,
  riskStatusOptions: RiskStatusOption[],
  countryIndexOptions: RiskStatusOption[],
  industryIndexOptions: RiskStatusOption[],
) => {
  const { formatMessage } = useIntl()
  const riskChips: React.ReactNode[] = []

  const countryRiskFilter = categoryFilters?.find(filter => filter.name === COUNTRY_RISK_PROP)
  const countryIndexFilter = categoryFilters?.find(filter => filter.name === COUNTRY_INDEX_PROP)

  const industryRiskFilter = categoryFilters?.find(filter => filter.name === INDUSTRY_RISK_PROP)
  const industryIndexFilter = categoryFilters?.find(filter => filter.name === INDUSTRY_INDEX_PROP)

  const generalCategoryFilters = extractCategoryFilters(categoryFilters)

  if (countryIndexFilter?.filters?.[0]?.value) {
    const countryIndexOption = countryIndexOptions.find(
      option => option.value === Number(countryIndexFilter.filters[0].value),
    )
    if (countryIndexOption) {
      riskChips.push(
        <StyledChip
          key="country-index"
          label={`${formatMessage({ id: 'schemas.risk.countryRiskIndex' })}: ${countryIndexOption.label}`}
          size="small"
        />,
      )
    }
  }

  if (countryRiskFilter?.filters?.[0]?.value) {
    const countryRiskValues = Array.isArray(countryRiskFilter.filters[0].value)
      ? countryRiskFilter.filters[0].value
      : [countryRiskFilter.filters[0].value]

    const countryRiskLabels = countryRiskValues
      .map(value => riskStatusOptions.find(option => option.value === value)?.label)
      .filter(Boolean)

    if (countryRiskLabels.length > 0) {
      riskChips.push(
        <StyledChip
          key="country-risk"
          label={`${formatMessage({ id: 'general.countryRisk' })}: ${countryRiskLabels.join(', ')}`}
          size="small"
        />,
      )
    }
  }

  if (industryIndexFilter?.filters?.[0]?.value) {
    const industryIndexOption = industryIndexOptions.find(
      option => option.value === Number(industryIndexFilter.filters[0].value),
    )
    if (industryIndexOption) {
      riskChips.push(
        <StyledChip
          key="industry-index"
          label={`${formatMessage({ id: 'schemas.risk.industryRiskIndex' })}: ${industryIndexOption.label}`}
          size="small"
        />,
      )
    }
  }

  if (industryRiskFilter?.filters?.[0]?.value) {
    const industryRiskValues = Array.isArray(industryRiskFilter.filters[0].value)
      ? industryRiskFilter.filters[0].value
      : [industryRiskFilter.filters[0].value]

    const industryRiskLabels = industryRiskValues
      .map(value => riskStatusOptions.find(option => option.value === value)?.label)
      .filter(Boolean)

    if (industryRiskLabels.length > 0) {
      riskChips.push(
        <StyledChip
          key="industry-risk"
          label={`${formatMessage({ id: 'general.industryRisk' })}: ${industryRiskLabels.join(', ')}`}
          size="small"
        />,
      )
    }
  }

  generalCategoryFilters.forEach(filter => {
    filter.filters.forEach((f, index) => {
      riskChips.push(
        <FilterDisplayChip
          key={`${filter.name}-${index}`}
          filterName={filter.name}
          filterValue={f.value}
          operator={f.operator}
        />,
      )
    })
  })

  return riskChips
}
